import { Component, EventEmitter, Input, Output } from '@angular/core';

import { ActionKeys } from '../../enums/keys.enum';
import { messageSeverityEnum } from '../../../Misc/enums/message-severity.enum';
import { DropdownOptionModel } from '../../../../Common/Forms/models/dropdown-option.model';
import { ButtonTypesEnum } from '../../enums/button-types.enum';

export interface DropdownSelectorOptions {
    options: DropdownOptionModel[];
    selectedOption: DropdownOptionModel;
}

interface FilterSelectListItem {
    id: string;
    name: string;
}

/**
 * ### Accessibility Usage
 * + ***id*** - provide an unique id so that this component can be correctly recognized by screen readers. Use interpolation [id]="'unique_id'"
 * + ***listAriaLabel*** - use this to provide an accessible label for the list
 * + ***textInputAriaLabel*** - use this to provide an accessible label for the text input that filters the list
 */
@Component({
    selector: 'agpr-list-filter-select',
    templateUrl: './list-filter-select.component.html',
    styleUrls: ['./list-filter-select.component.scss'],
    standalone: false
})
export class ListFilterSelectComponent<T extends FilterSelectListItem> {
    @Input() id: string;
    @Input() placeholder: string;
    @Input() textInputLabel: string;
    @Input() textInputAriaLabel: string;
    @Input() listAriaLabel: string;
    @Input() vsItemSize: number = 35;
    @Input() vsMinBufferPx: number = 350;
    @Input() vsMaxBufferPx: number = 700;
    @Input() showActionButton: boolean = false;
    @Input() actionButtonTooltip: string = '';
    @Input() set list(list: T[]) {
        this._list = list;
        this.listItems = list?.map(item => {
            return {
                id: item.id,
                name: item.name
            } as FilterSelectListItem;
        });
        if (this.listItems?.length > 0) {
            this.setFilteredListItems();
        }
    };
    @Input() noContentMessage: string;
    @Input() dropdownSelectorOptions: DropdownSelectorOptions = null;

    @Output() itemSelected: EventEmitter<T> = new EventEmitter<T>();
    @Output() dropdownChanged = new EventEmitter<DropdownOptionModel>();
    @Output() actionButtonClicked = new EventEmitter<void>();

    readonly messageSeverities = messageSeverityEnum;
    readonly buttonTypes = ButtonTypesEnum;

    searchText: string = '';
    _list: T[];
    listItems: FilterSelectListItem[];
    filteredListItems: FilterSelectListItem[];
    selectedListItem: FilterSelectListItem;

    constructor() {}

    setFilteredListItems(): void {
        this.filteredListItems = this.listItems?.filter(
            (listItem) => {
                return listItem.name.toLowerCase().indexOf(this.searchText.toLowerCase()) !== -1;
            }
        );
    }

    onItemSelected(item: FilterSelectListItem, event?: any): void {
        if (event instanceof KeyboardEvent && !(event.key === ActionKeys.ENTER || event.key === ActionKeys.SPACE)) {
            return;
        }
        this.selectedListItem = item;
        const output = this._list.find(listItem => listItem.id === item.id);
        this.itemSelected.emit(output);
    }

    onDropdownChanged(selectedOption: DropdownOptionModel) {
        this.dropdownChanged.emit(selectedOption);
    }
}
